import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { unlockDraw } from "@/lib/drawLookup";
import { useFilters } from "@/context/FiltersContext";
import { money, fmtDate, entryLink } from "@/lib/ui";
import { getChoices, cycleChoice, setPreference } from "@/lib/choices";
import ViewToolbar from "@/components/ViewToolbar";
import RodeoInfoDialog from "@/components/RodeoInfoDialog";
import { OfficialBox } from "@/components/OfficialControls";
import { MonthNav, monthsInRange } from "@/components/MonthNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Info, PlusCircle, Users, ListOrdered, ArrowLeftRight, ExternalLink, ChevronDown, ChevronLeft, ChevronRight, CalendarDays, LayoutList } from "lucide-react";
import { PickCircle } from "@/components/PickCircle";
import { toast } from "sonner";
import useIsMobile from "@/lib/useIsMobile";
import MobileCalendar from "@/pages/mobile/MobileCalendar";
import useTradeConflicts from "@/lib/useTradeConflicts";
import TradeFinderDialog from "@/components/TradeFinderDialog";

const DAY_W = 96, INFO_W = 268;
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const DLG = "bg-white text-[#14181F] border border-[#E4E7EB] max-w-lg";
const statusPill = (s) => s === "open" ? "bg-[#E7F6E1] text-[#3B6B1E]" : s === "closed" ? "bg-[#FDE7E9] text-[#9E0008]" : "bg-[#FFF3D6] text-[#8A6100]";

function ActBtn({ onClick, icon: Icon, label, testid, primary }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${primary ? "bg-[#C8000F] text-white hover:bg-[#9E0008]" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

export default function CalendarView() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileCalendar /> : <CalendarViewDesktop />;
}

function CalendarViewDesktop() {
  const nav = useNavigate();
  const { matches, dateRange, setCustomRange, rodeos } = useFilters();
  const [choices, setChoices] = useState(getChoices());
  const [dlg, setDlg] = useState(null); // {type, rodeo}
  const [peek, setPeek] = useState(null); // {date, list}

  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const months = monthsInRange(dateRange);

  const dayMap = useMemo(() => {
    const mp = {};
    for (const r of (rodeos || []).filter(matches)) {
      const seen = new Set();
      for (const p of (r.performances || [])) {
        if (p.date && !seen.has(p.date)) { seen.add(p.date); (mp[p.date] = mp[p.date] || []).push(r); }
      }
    }
    return mp;
  }, [rodeos, matches]);

  const onCell = (rid, pid) => { setChoices(cycleChoice(rid, pid)); };

  return (
    <div>
      <ViewToolbar active="/" title="Calendar" />

      <div className="px-4 sm:px-6 py-2.5 border-b border-[#E4E7EB] flex items-center justify-between gap-x-4 gap-y-2 flex-wrap" data-testid="cal-controls">
        <MonthNav />
      </div>

      {rodeos === null ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : (
        <div className="px-4 sm:px-6 py-4 space-y-6" data-testid="calendar-month">
          <div className="flex items-center gap-4 text-[12px] text-slate-500" data-testid="cal-legend">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#C8000F]" />CPRA</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1D4ED8]" />PRCA</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#9333EA]" />CCA</span>
          </div>
          {months.map((mo) => (
            <MonthCalendar key={`${mo.y}-${mo.m}`} y={mo.y} m={mo.m} dayMap={dayMap}
              onOpen={(r) => setDlg({ type: "info", rodeo: r })}
              onPeek={(dISO, list) => setPeek({ date: dISO, list })} />
          ))}
        </div>
      )}

      <DayPeekDialog peek={peek} onClose={() => setPeek(null)} onOpen={(r) => { setPeek(null); setDlg({ type: "info", rodeo: r }); }} />

      <RodeoInfoDialog data={dlg?.type === "info" ? dlg.rodeo : null} onClose={() => setDlg(null)} />
      <EnterDialog data={dlg?.type === "enter" ? dlg.rodeo : null} onClose={() => setDlg(null)} choices={choices} setChoices={setChoices} />
      <CountDialog data={dlg?.type === "count" ? dlg.rodeo : null} onClose={() => setDlg(null)} />
      <DrawDialog data={dlg?.type === "draw" ? dlg.rodeo : null} onClose={() => setDlg(null)} />
      <TradeFinderDialog rodeo={dlg?.type === "trade" ? dlg.rodeo : null} conflict={dlg?.conflict} onClose={() => setDlg(null)} />
    </div>
  );
}

const monthLabel = (k) => new Date(k + "-01T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" });

export function MonthCalendar({ y, m, dayMap, onOpen, onPeek, onEntry, onPick, onEntered, choices, highlightId }) {
  const startPad = new Date(y, m, 1).getDay();
  const daysIn = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const dot = (r) => (r.associations || []).includes("CPRA") ? "#C8000F" : (r.associations || []).includes("PRCA") ? "#1D4ED8" : "#9333EA";
  const todayISO = new Date().toISOString().slice(0, 10);
  const pad2 = (n) => String(n).padStart(2, "0");
  return (
    <div data-testid={`month-${y}-${pad2(m + 1)}`}>
      <div className="text-sm font-display font-bold text-slate-700 mb-2">{new Date(y, m, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
      <div className="grid grid-cols-7 gap-px bg-[#E4E7EB] border border-[#E4E7EB] rounded-lg overflow-hidden">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
          <div key={w} className="bg-[#F7F8FA] text-center text-[10px] font-bold uppercase tracking-wide text-slate-400 py-1.5">{w}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="bg-[#FBFCFD] h-[128px]" />;
          const dISO = `${y}-${pad2(m + 1)}-${pad2(d)}`;
          const list = dayMap[dISO] || [];
          const isToday = dISO === todayISO;
          return (
            <div key={i} className="bg-white h-[128px] p-1" data-testid={`day-${dISO}`}>
              <button onClick={() => list.length && onPeek(dISO, list)} disabled={!list.length}
                className={`text-[11px] font-bold mb-0.5 flex items-center justify-center ${isToday ? "text-white bg-[#C8000F] rounded-full w-5 h-5" : `text-slate-500 px-1 ${list.length ? "hover:text-[#C8000F] cursor-pointer" : ""}`}`}>{d}</button>
              <div className="space-y-0.5">
                {list.slice(0, 3).map((r) => {
                  const perf = (r.performances || []).find((p) => p.date === dISO);
                  const ch = (choices && perf) ? (choices[r.id] || {}) : {};
                  const num = perf ? (ch.pref1 === perf.id ? 1 : ch.pref2 === perf.id ? 2 : null) : null;
                  const ent = perf ? !!(ch.entered || {})[perf.id] : false;
                  const hot = highlightId === r.id;
                  return (
                  <div key={r.id} data-entry={r.id}
                    className={`w-full flex items-center gap-1 text-[10px] leading-tight px-1 py-0.5 rounded ${hot ? "ring-2 ring-[#C8000F] bg-[#FDECEE]" : ent ? "bg-[#DCF5D6] border border-[#166534]" : num === 1 ? "bg-[#EAF3EC] border border-[#166534]" : "bg-[#F7F8FA] hover:bg-[#EEF0F2]"}`}>
                    <button onClick={() => onEntry ? onEntry(r) : onOpen(r)} data-testid={`cal-chip-${dISO}-${r.id}`}
                      className="flex items-center gap-1 text-left min-w-0 flex-1">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dot(r) }} />
                      <span className="truncate">{r.name}</span>
                    </button>
                    {onPick && perf && <PickCircle num={num} entered={ent} onClick={() => onPick(r, perf)} onToggleEntered={onEntered ? () => onEntered(r, perf) : undefined} testid={`cal-pick-${dISO}-${r.id}`} size={14} />}
                  </div>
                  );
                })}
                {list.length > 3 && (
                  <button onClick={() => onPeek(dISO, list)} data-testid={`cal-more-${dISO}`}
                    className="text-[10px] text-[#C8000F] font-semibold px-1 hover:underline">+{list.length - 3} more</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DayPeekDialog({ peek, onClose, onOpen }) {
  const dot = (r) => (r.associations || []).includes("CPRA") ? "#C8000F" : (r.associations || []).includes("PRCA") ? "#1D4ED8" : "#9333EA";
  const title = peek ? new Date(peek.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";
  return (
    <Dialog open={!!peek} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DLG} data-testid="day-peek-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>{peek ? `${peek.list.length} rodeo${peek.list.length === 1 ? "" : "s"} on this day` : ""}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[62vh] overflow-y-auto space-y-1.5">
          {peek?.list.map((r) => (
            <button key={r.id} onClick={() => onOpen(r)} data-testid={`peek-row-${r.id}`}
              className="w-full flex items-center justify-between gap-3 text-left px-3 py-2.5 rounded-lg border border-[#E4E7EB] hover:bg-[#F7F8FA]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot(r) }} />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{r.name}</div>
                  <div className="text-[12px] text-[#C8000F] truncate">{r.city}, {r.state}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {(r.associations || []).map((a) => <span key={a} className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#D7DBE0] text-slate-600">{a}</span>)}
                <span className="text-[12px] font-semibold text-slate-700">{money(r.added_money)}</span>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AgendaRow({ r, choices, onCell, setDlg, nav }) {
  const ch = choices[r.id] || {};
  const st = r.status;
  const conflict = useTradeConflicts().conflictFor(r.id);
  return (
    <div className="border border-[#E4E7EB] rounded-lg p-4 hover:bg-[#FAFBFC]" data-testid={`agenda-row-${r.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => nav(`/rodeo/${r.id}`)} data-testid={`agenda-open-${r.id}`} className="text-left block">
            <div className="font-display font-bold text-[16px] leading-tight hover:text-[#C8000F]">{r.name}</div>
            <div className="text-[12px] text-[#C8000F] font-medium">{r.city}, {r.state}</div>
          </button>
          <div className="flex items-center gap-1 my-1.5 flex-wrap">
            {r.associations.map((a) => <span key={a} className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#D7DBE0] text-slate-600">{a}</span>)}
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${statusPill(st)}`}>{st === "open" ? "OPEN" : st === "closed" ? "CLOSED" : "SOON"}</span>
            <span className="text-[12px] font-semibold text-slate-700 ml-1">{money(r.added_money)} Added</span>
          </div>
          <div className="text-[11px] text-slate-400">EO {fmtDate(r.entry_open)} &nbsp;•&nbsp; EC {fmtDate(r.entry_close)}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex flex-nowrap items-center gap-1 justify-end overflow-x-auto">
            {st !== "closed" && <ActBtn onClick={() => setDlg({ type: "info", rodeo: r })} icon={Info} label="Info" testid={`info-btn-${r.id}`} />}
            {st !== "closed" && <ActBtn onClick={() => window.open(entryLink(r), "_blank")} icon={PlusCircle} label="Enter" testid={`enter-btn-${r.id}`} primary />}
            {st === "open" && <ActBtn onClick={() => setDlg({ type: "count", rodeo: r })} icon={Users} label="Count" testid={`count-btn-${r.id}`} />}
            {st === "closed" && <ActBtn onClick={() => setDlg({ type: "info", rodeo: r })} icon={Info} label="Info" testid={`info-btn-${r.id}`} />}
            {st === "closed" && <ActBtn onClick={() => setDlg({ type: "draw", rodeo: r })} icon={ListOrdered} label="Draw" testid={`draw-btn-${r.id}`} />}
            {conflict && <ActBtn onClick={() => setDlg({ type: "trade", rodeo: r, conflict })} icon={ArrowLeftRight} label="Trade" testid={`trade-btn-${r.id}`} primary={!!conflict.conflicts_with?.length} />}
          </div>
          <OfficialBox rodeoId={r.id} />
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(r.performances || []).map((p) => {
          const is1 = ch.pref1 === p.id, is2 = ch.pref2 === p.id;
          return (
            <button key={p.id} onClick={() => onCell(r.id, p.id)} data-testid={`agenda-cell-${r.id}-${p.id}`}
              className={`relative flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors ${is1 ? "bg-[#DCEFC4] border-[#8CC152]" : "bg-white border-[#D7DBE0] hover:border-slate-400"}`}>
              {(is1 || is2) && <span className="absolute -top-1.5 -right-1 text-[11px] font-extrabold text-[#C8000F]">{is1 ? "1" : "2"}</span>}
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${p.type === "slack" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>
              <span className="text-[11px] font-semibold text-slate-800">{p.label}</span>
              <span className="text-[10px] text-slate-500">{fmtDate(p.date)} · {p.time}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AgendaList({ groups, choices, onCell, setDlg, nav }) {
  const [collapsed, setCollapsed] = useState({});
  if (!groups.length) return <div className="text-center py-24 text-slate-400" data-testid="calendar-agenda-empty">No rodeos in this date range. Adjust your season or filters.</div>;
  const toggle = (k) => setCollapsed((p) => ({ ...p, [k]: !p[k] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => { const m = {}; groups.forEach((g) => { m[g.key] = true; }); setCollapsed(m); };
  return (
    <div className="px-4 sm:px-6 py-4 space-y-3" data-testid="calendar-agenda">
      <div className="flex items-center gap-2">
        <button onClick={expandAll} data-testid="agenda-expand-all"
          className="text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400">Expand all</button>
        <button onClick={collapseAll} data-testid="agenda-collapse-all"
          className="text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400">Collapse all</button>
      </div>
      {groups.map((g) => {
        const open = !collapsed[g.key];
        return (
          <div key={g.key}>
            <button onClick={() => toggle(g.key)} data-testid={`agenda-month-${g.key}`}
              className="flex items-center gap-2 w-full text-left py-1.5 border-b border-[#EEF0F2] mb-2 group">
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600 group-hover:text-slate-900">{monthLabel(g.key)}</span>
              <span className="text-slate-300 text-xs">· {g.items.length} rodeo{g.items.length === 1 ? "" : "s"}</span>
            </button>
            {open && (
              <div className="space-y-2">
                {g.items.map((r) => <AgendaRow key={r.id} r={r} choices={choices} onCell={onCell} setDlg={setDlg} nav={nav} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-md bg-[#F7F8FA] px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-[#14181F] font-medium mt-0.5">{value}</div>
    </div>
  );
}

function DrawDialog({ data, onClose }) {
  const nav = useNavigate();
  const [draw, setDraw] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (!data) return;
    setDraw(null); setLocked(false);
    unlockDraw(data.id).then((res) => {
      if (!res.ok) { setLocked(true); return; }
      api.get(`/rodeos/${data.id}/draw`).then((r) => setDraw(r.data)).catch(() => setDraw({ real_draw: false, events: [] }));
    });
  }, [data]);
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="draw-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Draw · {data.name}</DialogTitle>
              <DialogDescription>Who's up, by event and performance</DialogDescription>
            </DialogHeader>
            {locked ? (
              <div className="text-center py-8 px-4" data-testid="draw-paywall">
                <p className="text-sm text-slate-600">You've used your free draw lookup. Unlock this draw with 1 credit, or subscribe for unlimited.</p>
                <button onClick={() => { onClose(); nav("/choose-plan"); }} data-testid="draw-see-plans" className="mt-3 bg-[#C8000F] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">See plans</button>
              </div>
            ) : !draw ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
              : !draw.real_draw || !(draw.events || []).length ? (
                <p className="text-sm text-slate-500 py-4">The draw hasn't been posted yet for this rodeo. Check back closer to the performance dates.</p>
              ) : (
                <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                  {draw.events.map((ev) => (
                    <div key={ev.event} className="rounded-md border border-[#EEF0F2] p-3">
                      <div className="text-sm font-semibold mb-2 flex items-center justify-between"><span>{ev.event}</span><span className="text-xs text-slate-400">{ev.total} entered</span></div>
                      <div className="space-y-1">
                        {ev.rows.slice(0, 60).map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-[#F7F8FA]">
                            <span className="text-slate-700"><b>{r.position}.</b> {ev.team ? `${r.header || "?"} / ${r.heeler || "?"}` : r.contestant}</span>
                            <span className="text-slate-400 text-right">{r.performance_label}{r.stock ? ` · ${r.stock}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            <button onClick={() => { onClose(); nav(`/rodeo/${data.id}`); }} data-testid="draw-open-full"
              className="w-full mt-3 border border-[#D7DBE0] text-slate-700 hover:bg-[#F1F3F5] text-sm font-semibold py-2.5 rounded-lg">Open full draw sheet</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EnterDialog({ data, onClose, choices, setChoices }) {
  if (!data) return <Dialog open={false} onOpenChange={onClose}><DialogContent className={DLG} /></Dialog>;
  const ch = choices[data.id] || {};
  const set = (pid, n) => { setChoices(setPreference(data.id, pid, n)); toast.success(`Set ${n === 1 ? "1st" : "2nd"} preference`); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="enter-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Enter · {data.name}</DialogTitle>
          <DialogDescription>Choose which performance you want to enter — set 1st and 2nd preference.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[46vh] overflow-y-auto">
          {data.performances.map((p) => {
            const is1 = ch.pref1 === p.id, is2 = ch.pref2 === p.id;
            return (
              <div key={p.id} className={`flex items-center justify-between py-2 px-3 rounded-md border ${is1 ? "bg-[#DCEFC4] border-[#8CC152]" : "bg-[#F7F8FA] border-transparent"}`}>
                <div>
                  <div className="text-sm font-medium flex items-center gap-2"><span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${p.type === "slack" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>{p.label}</div>
                  <div className="text-xs text-slate-500">{fmtDate(p.date)} · {p.time}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => set(p.id, 1)} data-testid={`enter-pref1-${p.id}`} className={`text-[11px] px-2 py-1 rounded font-semibold ${is1 ? "bg-[#C8000F] text-white" : "bg-white border border-[#D7DBE0] text-slate-600"}`}>1st</button>
                  <button onClick={() => set(p.id, 2)} data-testid={`enter-pref2-${p.id}`} className={`text-[11px] px-2 py-1 rounded font-semibold ${is2 ? "bg-[#C8000F] text-white" : "bg-white border border-[#D7DBE0] text-slate-600"}`}>2nd</button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CountDialog({ data, onClose }) {
  const { events: myEvents } = useFilters();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (data) { setCounts(null); api.get(`/rodeos/${data.id}/count`).then((r) => setCounts(r.data)).catch(() => setCounts({})); }
  }, [data]);
  const evList = data ? data.events.filter((ev) => myEvents.size === 0 || myEvents.has(ev)) : [];
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="count-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Entry Count · {data.name}</DialogTitle>
              <DialogDescription>How the field is filling up per performance.</DialogDescription>
            </DialogHeader>
            {!counts ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div> : (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {data.performances.map((p) => (
                  <div key={p.id} className="rounded-md border border-[#EEF0F2] p-3">
                    <div className="text-sm font-semibold mb-2 flex items-center justify-between"><span>{p.label}</span><span className="text-xs text-slate-400">{fmtDate(p.date)}</span></div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {evList.map((ev) => (
                        <div key={ev} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-[#F7F8FA]">
                          <span className="text-slate-500 truncate mr-2">{ev}</span>
                          <span className="text-[#C8000F] font-bold">{counts[p.id]?.[ev] ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
